/**
 * App layout: fixed sidebar (collapsible on mobile) + top header carrying the
 * theme/language toggles, backend-health indicator and the user menu. The nav is
 * built from the feature registry filtered by the enabled flags + caller roles, so
 * it stays in lock-step with the mounted routes. Logical properties keep the
 * sidebar on the inline-start edge in both LTR and RTL.
 */
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";
import Brand from "@/components/Brand";
import { LanguageToggle } from "@/components/LanguageToggle";
import { ThemeToggle } from "@/components/ThemeToggle";
import { UserMenu } from "@/components/UserMenu";
import { enabledFeatures, enabledShellWidgets } from "@/features/registry";
import { NAV_GROUPS } from "@/features/types";
import { ScopeBar } from "@/scope/ScopeBar";
import { DockProvider, useDockInset } from "@/shell/dock";
import { IntentProvider } from "@/shell/intents";
import { useHealth } from "@/lib/queries";

function HealthDot() {
  const { t } = useTranslation();
  const { data, isError } = useHealth();
  const ok = !isError && data?.status === "ok" && data?.db;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted">
      <span
        aria-hidden="true"
        className="h-2 w-2 rounded-full"
        style={{
          backgroundColor: ok
            ? "rgb(var(--color-status-ok))"
            : "rgb(var(--color-status-critical))",
        }}
      />
      <span>{ok ? t("dashboard.connected") : t("dashboard.disconnected")}</span>
    </span>
  );
}

/** Always-visible data-plane badge so operators never mistake synthetic for real. */
function DataSourceBadge() {
  const { t } = useTranslation();
  const { dataSourceBanner, isSynthetic } = useAuth();
  return (
    <span
      className="hidden items-center rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-fg-muted sm:inline-flex"
      style={
        isSynthetic
          ? undefined
          : {
              color: "rgb(var(--color-status-ok))",
              borderColor: "rgb(var(--color-status-ok))",
            }
      }
      title={isSynthetic ? t("dashboard.syntheticDataHint") : t("dashboard.realDataHint")}
    >
      {dataSourceBanner}
    </span>
  );
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { features, user } = useAuth();
  const items = enabledFeatures(features, user).filter((f) => f.showInNav !== false);

  // Grouped by each feature's declared section, in NAV_GROUPS order. The shell
  // names no feature and no group membership — adding a destination stays a
  // one-line descriptor change, exactly as with the flat list it replaces.
  // A group with nothing enabled for this caller renders nothing, heading included.
  const sections = NAV_GROUPS.map((group) => ({
    group,
    items: items.filter((f) => f.group === group),
  })).filter((s) => s.items.length > 0);

  return (
    <nav aria-label={t("nav.primary")} className="flex flex-col gap-4 p-3">
      {sections.map(({ group, items: groupItems }) => (
        <div key={group} className="flex flex-col gap-1">
          <h2 className="px-3 pb-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-fg-subtle">
            {t(`nav.groups.${group}`)}
          </h2>
          {groupItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.id}
                to={item.path}
                end={item.index}
                onClick={onNavigate}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-brand/10 text-brand"
                      : "text-fg-muted hover:bg-bg-subtle hover:text-fg",
                  ].join(" ")
                }
              >
                <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                <span>{t(item.navKey)}</span>
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

/**
 * Feature-owned header surfaces (see ShellWidgetDescriptor). Rendered
 * generically so the shell never depends on any particular feature; a widget
 * that fails to load must not take the header down with it, hence the null
 * fallback rather than a skeleton.
 */
function ShellWidgets() {
  const { features, user } = useAuth();
  const widgets = enabledShellWidgets(features, user);
  return (
    <>
      {widgets.map((widget) => {
        const Widget = widget.element;
        return (
          <Suspense key={widget.id} fallback={null}>
            <Widget />
          </Suspense>
        );
      })}
    </>
  );
}

/**
 * Shell services wrap the layout rather than sit inside it: a header widget and
 * a routed page are siblings, so anything they both need has to be provided
 * above the two of them.
 */
export function AppShell() {
  return (
    <DockProvider>
      <IntentProvider>
        <ShellLayout />
      </IntentProvider>
    </DockProvider>
  );
}

function ShellLayout() {
  const { t } = useTranslation();
  const { appName, appTagline } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Space claimed at the inline-end edge by a docked shell surface. The shell
  // never learns which surface is asking — see shell/dock.
  const dockInset = useDockInset();

  return (
    <div className="min-h-screen bg-bg text-fg">
      {/* Sidebar — persistent on lg, drawer on mobile */}
      <aside className="fixed inset-y-0 start-0 z-30 hidden w-60 border-e border-border bg-bg-elevated lg:block">
        <div className="flex h-16 items-center gap-2 border-b border-border px-5">
          <Brand size={32} />
          <div className="leading-tight">
            <p className="text-sm font-semibold text-fg">{appName}</p>
            <p className="text-[11px] text-fg-subtle">
              {appTagline || t("app.tagline")}
            </p>
          </div>
        </div>
        <SidebarNav />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label={t("actions.closeMenu")}
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 start-0 w-64 border-e border-border bg-bg-elevated">
            <div className="flex h-16 items-center justify-between border-b border-border px-4">
              <span className="flex items-center gap-2">
                <Brand size={24} />
                <span className="text-sm font-semibold text-fg">{appName}</span>
              </span>
            </div>
            <SidebarNav onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main column. The dock inset is a margin rather than padding so the
          sticky header shrinks with the content instead of sliding underneath
          a docked panel. */}
      <div
        className="transition-[margin] duration-200 lg:ms-60"
        style={dockInset > 0 ? { marginInlineEnd: dockInset } : undefined}
      >
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-border bg-bg-elevated/95 px-4 backdrop-blur sm:px-6">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={t("actions.openMenu")}
              aria-expanded={mobileOpen}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-fg-muted hover:bg-bg-subtle lg:hidden"
              onClick={() => setMobileOpen(true)}
            >
              <span aria-hidden="true">☰</span>
            </button>
            <HealthDot />
            <DataSourceBadge />
          </div>
          <div className="hidden flex-1 items-center justify-center md:flex">
            <ScopeBar />
          </div>
          <div className="flex items-center gap-2">
            <ShellWidgets />
            <LanguageToggle />
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
